/**
 * @file index.tsx
 *
 * @fileoverview Renders the landing page regional jobs paragraph.
 */
import { Job, RegionalJobsParagraph } from '~/jobiqo/types/types';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import React from 'react';
import { JobTeasersGrid } from '~/jobiqo/components/Landingpage/JobsTeasersGrid';

interface RegionalJobsProps {
  /**
   * The regional jobs paragraph row.
   */
  row: RegionalJobsParagraph;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export default function RegionalJobs({
  row,
  maxContainerSize,
}: RegionalJobsProps) {
  const geoInput = {
    locationType: 'locality',
    lat: row.lat,
    lon: row.lon,
    radius: row.radius,
  };

  const showAllJobsQueryParams = new URLSearchParams({
    ...Object.fromEntries(
      Object.entries(geoInput).map(([key, value]) => [key, String(value)]),
    ),
    // We only search for the jobs based on the lat/lon, but need this param
    // as well for the job search query string.
    geo_location: row?.location ?? '',
  }).toString();

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={row.spacing ?? 12}
      paddingBottom={row.spacing ?? 12}
      {...(row.background && {
        backgroundColorOverride: `var(--color-${row.background})`,
      })}
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <FlexItems justifyContent="space-between" alignItems="center">
          <FlexItem>
            {row.regionalJobsTitle && (
              <>
                <Text
                  color="primary"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  {row.regionalJobsSubTitle}
                </Text>
                <Spacing size="medium" />
              </>
            )}
            {row.regionalJobsTitle && (
              <>
                <Text tag="h2" fontSize={[5, 6]} lineHeight={[5, 6]}>
                  {row.regionalJobsTitle}
                </Text>
              </>
            )}
          </FlexItem>
        </FlexItems>
        <JobTeasersGrid
          jobs={(row.jobdata?.pages as [Job]) ?? []}
          paragraphInfo={row}
          maxContainerSize={maxContainerSize}
          showAllJobsQueryParams={showAllJobsQueryParams}
        />
      </Main>
    </Box>
  );
}
export { RegionalJobs };
